import { useMemo, useState } from "react";
import type { DateTime } from "luxon";
import { isDateTime } from "@cpt/shared";
import useQueryParams from "hooks/useQueryParams";
import type { FilterQuery } from "client/api/common/FilterQuery";
import type { FilterQueryOperator } from "client/api/common/FilterQuery";

export interface FilterStateValue {
  operator: FilterQueryOperator;
  targetProperty: string;
}

export type FilterValue = string | boolean | DateTime | undefined | null;
export type FilterState = {
  [key: string]: FilterValue;
};

// filterMap is for pages whose filters build a generic FilterQuery.
// paramKeys is for pages whose filters go straight into named API params and have no use for a FilterQuery.
type UseFiltersProps = { filterMap: { [key: string]: FilterStateValue } } | { paramKeys: string[] };

interface UseFiltersModel {
  filters: FilterState;
  filterQuery: FilterQuery;
  setFilter: (key: string, value: FilterValue) => void;
  resetFilter: () => void;
}

function useFilters(props: UseFiltersProps): UseFiltersModel {
  const filterMap = "filterMap" in props ? props.filterMap : undefined;
  const paramKeys = "filterMap" in props ? Object.keys(props.filterMap) : props.paramKeys;

  const [parameters, setParameters] = useQueryParams({ paramKeys });
  const [filters, setFiltersState] = useState<FilterState>(parameters);

  const filterQuery = useMemo(() => {
    if (!filterMap) {
      return {};
    }

    return Object.entries(filters).reduce((acc: FilterQuery, [currKey, currVal]) => {
      const foundLookup = filterMap[currKey];

      if (foundLookup) {
        if (isDateTime(currVal)) {
          acc[foundLookup.targetProperty] = {
            ...acc[foundLookup.targetProperty],
            [foundLookup.operator]: currVal.isValid ? currVal.toUTC().toISODate() : undefined,
          };
        } else {
          acc[foundLookup.targetProperty] = {
            ...acc[foundLookup.targetProperty],
            [foundLookup.operator]: currVal,
          };
        }
      }

      return acc;
    }, {});
  }, [filters, filterMap]);

  const setFilter = (key: string, value: FilterValue) => {
    const updatedFilters = { ...filters };
    if (value) {
      updatedFilters[key] = value;
    } else {
      delete updatedFilters[key];
    }
    setFiltersState(updatedFilters);
    setParameters(updatedFilters);
  };

  const resetFilter = () => {
    setFiltersState({});
    setParameters({});
  };

  return { filters, filterQuery, setFilter, resetFilter };
}

export default useFilters;
